import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import SaleListService from "../../services/SaleListService";
import FinancialAccountService from "../../services/FinancialAccountService";
import api from "../../api/axios";
// src/pages/Sales/components/SalesPaymentsDialog.jsx

const currency = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY" });
const fmtMoney = (v) => currency.format(v ?? 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "");
 
const PAYMENT_METHODS = [
    { label: "Nakit", value: "Cash" },
    { label: "Kredi Kartı", value: "Card" },
    { label: "Çek", value: "Çek" },
    { label: "Senet", value: "Senet" },
    { label: "Havale/EFT", value: "Transfer" },
    { label: "Diğer", value: "Other" },
];

export default function SalesPaymentsDialog({
  saleId,
  visible,
  onHide,
  onChanged, // ödeme eklendiğinde üst listeyi yenilemek için callback
}) {
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // Yeni ödeme formu
  const [addOpen, setAddOpen] = useState(false);
  const [method, setMethod] = useState(null);
  const [amount, setAmount] = useState(null);
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [referenceNo, setReferenceNo] = useState("");
  const [note, setNote] = useState("");
  const [financialAccounts, setFinancialAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  const [chequeDetails, setChequeDetails] = useState({
    bankName: "",
    bankBranch: "",
    checkNumber: "",
    dueDate: null,
    drawer: ""
  });

  const [promissoryDetails, setPromissoryDetails] = useState({
    dueDate: null,
    drawer: "",
    guarantor: ""
  });

  useEffect(() => {
    if (visible) {
      loadPayments();
      FinancialAccountService.getAll().then(res => {
        if (res.success) {
          setFinancialAccounts(res.data || []);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, saleId]);

  const filteredAccounts = React.useMemo(() => {
    if (method === "Cash") return financialAccounts.filter(a => a.accountType === 1);
    if (method === "Transfer") return financialAccounts.filter(a => a.accountType === 2);
    if (method === "Card") return financialAccounts.filter(a => a.accountType === 3);
    return [];
  }, [financialAccounts, method]);

  useEffect(() => {
    if (filteredAccounts.length === 1) {
      setSelectedAccountId(filteredAccounts[0].id);
    } else {
      setSelectedAccountId(null);
    }
  }, [filteredAccounts]);

  const loadPayments = async () => {
 
    try {
      setLoading(true);
      const res = await SaleListService.getPayments(saleId);
      setRows(Array.isArray(res) ? res : []);
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: err?.message || "Ödemeler alınamadı.",
      });
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setMethod(null);
    setAmount(null);
    setPaymentDate(new Date());
    setReferenceNo("");
    setNote("");
  };

  const savePayment = async () => {
    // basit validasyon
    if (!method) {
      toast.current?.show({ severity: "warn", summary: "Uyarı", detail: "Ödeme yöntemi seçin." });
      return;
    }
    if (!amount || amount <= 0) {
      toast.current?.show({ severity: "warn", summary: "Uyarı", detail: "Tutar girin." });
      return;
    }
    try { 
      setLoading(true);
      const payload = {
        method,
        amount,
        paymentDate: paymentDate?.toISOString(), // backend DateTime alıyor
        referenceNo,
        note,
        financialAccountId: selectedAccountId,
        ...(method === "Çek" ? chequeDetails : {}),
        ...(method === "Senet" ? promissoryDetails : {}),
      };
      await SaleListService.addPayment(saleId, payload);
      // Başarılı
      toast.current?.show({ severity: "success", summary: "Kaydedildi", detail: "Ödeme eklendi." });
      setAddOpen(false);
      resetForm();
      await loadPayments();   // listeyi yenile
      onChanged?.();          // üst grid’i yenilemek istersen
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: err?.response?.data?.message || err?.message || "Ödeme eklenemedi.",
      });
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button label="Kapat" icon="pi pi-times" text onClick={onHide} />
      <Button label="Yeni Ödeme" icon="pi pi-plus" onClick={() => setAddOpen(true)} />
    </div>
  );

  const addFooter = (
    <div className="flex justify-end gap-2">
      <Button label="Vazgeç" icon="pi pi-times" text onClick={() => { setAddOpen(false); resetForm(); }} />
      <Button label="Kaydet" icon="pi pi-check" onClick={savePayment} />
    </div>
  );

  const methodBody = (row) => {
    const m = PAYMENT_METHODS.find((x) => x.value === (row.method ?? row.Method));
    return <Tag value={m?.label || row.method || row.Method} />;
  };

  return (
    <>
      <Toast ref={toast} />

      {/* Ödeme Detay Dialog */}
      <Dialog
        header={`Ödemeler ${rows.length === 0 ? "(Henüz ödeme yok)" : ""}`}
        visible={visible}
        onHide={onHide}
        style={{ width: "700px", maxWidth: "95vw" }}
        footer={footer}
        modal
      >
        <DataTable
          value={rows}
          loading={loading}
          stripedRows
          showGridlines
          emptyMessage="Ödeme kaydı bulunamadı."
        >
          <Column field="paymentDate" header="Tarih" body={(r) => fmtDate(r?.paymentDate || r.PaymentDate)} />
          <Column field="method" header="Yöntem" body={methodBody} />
          <Column field="amount" header="Tutar" body={(r) => fmtMoney(r?.amount ?? r.Amount)} />
          <Column field="referenceNo" header="Ref. No" />
          <Column field="note" header="Açıklama" />
        </DataTable>
      </Dialog>

      {/* Yeni Ödeme Ekle Dialog */}
      <Dialog
        header="Yeni Ödeme"
        visible={addOpen}
        onHide={() => setAddOpen(false)}
        style={{ width: "720px", maxWidth: "95vw" }}
        footer={addFooter}
        modal
      >
        <div className="p-fluid grid">
          <div className="col-12">
            <label className="block mb-1">Ödeme Yöntemi</label>
            <Dropdown
              value={method}
              options={PAYMENT_METHODS}
              onChange={(e) => setMethod(e.value)}
              placeholder="Seçin"
              className="w-full"
              appendTo="self" 
            />
          </div>
          {filteredAccounts.length > 0 && (
            <div className="col-12">
              <label className="block mb-1">İşlem Yapılacak Hesap</label>
              <Dropdown
                value={selectedAccountId}
                options={filteredAccounts}
                optionLabel="name"
                optionValue="id"
                onChange={(e) => setSelectedAccountId(e.value)}
                placeholder="Hesap seçin..."
                className="w-full"
              />
            </div>
          )}
          <div className="col-12">
            <label className="block mb-1">Tutar</label>
            <InputNumber
              value={amount}
              onValueChange={(e) => setAmount(e.value)}
              mode="currency"
              currency="TRY"
              locale="tr-TR"
              className="w-full"
            />
          </div>
          <div className="col-12">
            <label className="block mb-1">Tarih</label>
            <Calendar
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.value ?? new Date())}
              showIcon
              readOnlyInput
              dateFormat="dd.mm.yy"
              appendTo={document.body}
              className="w-full"
            />
          </div>
          <div className="col-12">
            <label className="block mb-1">Ref. No</label>
            <InputText value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} />
          </div>
          <div className="col-12">
            <label className="block mb-1">Açıklama</label>
            <InputText value={note} onChange={(e) => setNote(e.target.value)} />
          </div>

          {method === "Çek" && (
            <div className="col-12 grid">
              <div className="col-6"><label className="block mb-1">Banka</label><InputText value={chequeDetails.bankName} onChange={e => setChequeDetails({...chequeDetails, bankName: e.target.value})} /></div>
              <div className="col-6"><label className="block mb-1">Şube</label><InputText value={chequeDetails.bankBranch} onChange={e => setChequeDetails({...chequeDetails, bankBranch: e.target.value})} /></div>
              <div className="col-6"><label className="block mb-1">Çek No</label><InputText value={chequeDetails.checkNumber} onChange={e => setChequeDetails({...chequeDetails, checkNumber: e.target.value})} /></div>
              <div className="col-6"><label className="block mb-1">Vade</label><Calendar value={chequeDetails.dueDate} onChange={e => setChequeDetails({...chequeDetails, dueDate: e.value})} dateFormat="dd.mm.yy" showIcon appendTo={document.body} /></div>
              <div className="col-12"><label className="block mb-1">Keşideci</label><InputText value={chequeDetails.drawer} onChange={e => setChequeDetails({...chequeDetails, drawer: e.target.value})} /></div>
            </div>
          )}

          {method === "Senet" && (
            <div className="col-12 grid">
              <div className="col-6"><label className="block mb-1">Vade</label><Calendar value={promissoryDetails.dueDate} onChange={e => setPromissoryDetails({...promissoryDetails, dueDate: e.value})} dateFormat="dd.mm.yy" showIcon appendTo={document.body} /></div>
              <div className="col-6"><label className="block mb-1">Borçlu</label><InputText value={promissoryDetails.drawer} onChange={e => setPromissoryDetails({...promissoryDetails, drawer: e.target.value})} /></div>
              <div className="col-12"><label className="block mb-1">Kefil</label><InputText value={promissoryDetails.guarantor} onChange={e => setPromissoryDetails({...promissoryDetails, guarantor: e.target.value})} /></div>
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}
